/**
 * window.__DW — the deterministic automation surface.
 *
 * Every critic/QA agent drives the game through this API instead of relying on
 * wall-clock timing, so captures are reproducible regardless of how slow the
 * headless GPU is. In headless mode the rAF loop is NOT started; frames only
 * advance when step()/run() is called.
 */
export function installHarness(engine, world, opts = {}) {
  const api = {
    version: 1,
    engine, world,
    booted: false,
    headless: !!opts.headless,
    error: null,

    /** Advance exactly one frame of `dt` seconds and render. */
    step(dt = 1 / 60) { engine.advance(dt); return engine.time.frame; },
    /** Advance `seconds` of simulated time in fixed `dt` frames. */
    run(seconds, dt = 1 / 60) { const n = Math.max(1, Math.round(seconds / dt)); for (let i = 0; i < n; i++) engine.advance(dt); return n; },
    /**
     * Same simulation as run(), but only the last `tail` frames are drawn.
     *
     * A capture spends nearly all of its time travelling to the moment it wants
     * to photograph, and on this box every one of those frames is a full
     * software rasterisation. warp() skips the draw for the travel and renders
     * a tail so TAA history, motion vectors and the rest of the post chain's
     * frame-to-frame feedback converge exactly as they would have. The sim, the
     * camera solve and the RNG stream are bit-identical to run().
     *
     * Use it for the long approach; use run() for anything you are about to
     * shoot. A tail of 8 is enough for the current chain -- if you shorten it,
     * check a warp/run pair of the same frame against each other first.
     */
    warp(seconds, dt = 1 / 60, tail = 8) {
      const n = Math.max(1, Math.round(seconds / dt));
      const draw = Math.min(n, Math.max(0, tail));
      for (let i = 0; i < n; i++) engine.advance(dt, i < n - draw);
      return n;
    },
    /** Set the virtual input state, e.g. {move:{x:0,y:-1}, press:['light']}. */
    input(v) { world.input._virtual = v; },
    clearInput() { world.input._virtual = null; },
    tap(name, frames = 1) {
      world.input._virtual = { ...(world.input._virtual || {}), press: [name] };
      for (let i = 0; i < frames; i++) engine.advance(1 / 60);
      world.input._virtual = { ...(world.input._virtual || {}), press: [], release: [name] };
      engine.advance(1 / 60);
      world.input._virtual = { ...(world.input._virtual || {}), release: [] };
    },
    hold(name) { const v = world.input._virtual || (world.input._virtual = {}); v.press = [...new Set([...(v.press || []), name])]; v.release = []; },
    letGo(name) { const v = world.input._virtual || (world.input._virtual = {}); v.press = (v.press || []).filter(n => n !== name); v.release = [name]; },
    move(x, y) { const v = world.input._virtual || (world.input._virtual = {}); v.move = { x, y }; },
    look(x, y) { const v = world.input._virtual || (world.input._virtual = {}); v.look = { x, y }; },

    camera(yaw, pitch, dist) {
      const r = world.camrig; if (!r) return;
      if (yaw !== undefined) r.yaw = yaw;
      if (pitch !== undefined) r.pitch = pitch;
      if (dist !== undefined) r.dist = dist;
    },
    teleport(x, z) { const e = world.entities, id = world.playerId; e.x[id] = x; e.z[id] = z; e.y[id] = world.terrain.heightAt(x, z); },
    setQuality(q) { world.bus.emit('quality', q); },
    seed(n) { world.rng.seed(n); },

    stats() {
      const e = world.entities, p = world.perf;
      return {
        frame: engine.time.frame, sim: +engine.time.sim.toFixed(3), real: +engine.time.real.toFixed(3),
        fps: +p.fps.toFixed(1), cpuMs: +p.cpuMs.toFixed(2), drawCalls: p.drawCalls, triangles: p.triangles,
        geometries: p.geometries, textures: p.textures,
        entities: e.stats.total, byTeam: [...e.stats.byTeam], kills: world.player?.kills ?? 0,
        crowdVisible: world.crowd?.visible ?? 0,
        player: world.playerId >= 0 ? {
          x: +e.x[world.playerId].toFixed(2), y: +e.y[world.playerId].toFixed(2), z: +e.z[world.playerId].toFixed(2),
          hp: +(world.player?.hp ?? 0).toFixed(1), musou: +(world.player?.musou ?? 0).toFixed(1),
          state: e.state[world.playerId], combo: world.player?.combo ?? 0,
        } : null,
        mission: world.mission ? { state: world.mission.state, elapsed: +world.mission.elapsed.toFixed(2) } : null,
        errors: api.errors.slice(0, 20),
      };
    },
    errors: [],
    /** Scenario presets used by capture scripts; systems may register more. */
    scenarios: {},
    scenario(name, ...args) {
      const s = api.scenarios[name];
      if (!s) throw new Error('unknown scenario: ' + name);
      return s(world, ...args);
    },
  };
  window.__DW = api;
  window.addEventListener('error', (e) => api.errors.push(String(e.message || e)));
  window.addEventListener('unhandledrejection', (e) => api.errors.push('promise: ' + String(e.reason)));
  return api;
}
